/**
 * VAULT_GUIDED_ITEMS
 *
 * Sits ON TOP of the existing VAULT_CATEGORIES / VAULT_SUBCATEGORIES constants
 * (src/App.jsx lines 10388–10421). Does NOT replace or rename any existing
 * category/subcategory id, so no data migration is needed for existing
 * vault_items rows.
 *
 * Purpose: give the guided vault-upload flow a granular, orderable list of
 * specific things to ask the user about — one prompt per real-world document
 * or piece of info, each tagged with a priority tier and (where relevant)
 * guidance for what to do if the user doesn't have it.
 *
 * Fields:
 *  - id:        stable unique key (never reuse/repurpose after ship — user
 *               progress will likely be tracked against these ids)
 *  - category:  matches VAULT_CATEGORIES[].id exactly
 *  - subcategory: matches a string in VAULT_SUBCATEGORIES[category] exactly
 *               (one exception — see NOTE on digital_crypto_access below)
 *  - tier:      1 = immediate access (first 72 hrs), 2 = financial/legal
 *               backbone, 3 = completeness, 'legacy' = excluded from the
 *               tiered progress/time-saved model entirely (Personal Messages)
 *  - conditional: null, or a flag name the app should check before showing
 *               this item. "has_business" is NOT a stored DB/profile field —
 *               there is no such field anywhere in the schema (confirmed by
 *               grepping onboarding + saveEstate). It's satisfied client-side
 *               at runtime by the user's answer to the "business_owner_gate"
 *               item below, held in the guided-flow component's own state
 *               (or localStorage), not persisted to the estate/profile record.
 *  - kind:      "document" (default) = a normal upload/record prompt.
 *               "gate" = a yes/no question with no upload of its own, used
 *               only to reveal/hide other conditional items. Gate items have
 *               no meaningful noDocGuidance and no real subcategory tie-in.
 *               Only "business_owner_gate" and the 4 business "document"
 *               items have an explicit `kind` set below — every other item
 *               in this file omits the field and should be treated as
 *               `kind === "document"` by default when reading it.
 *  - prompt:    the user-facing question shown in the guided flow
 *  - noDocGuidance: shown when the user selects "I don't have this" —
 *               who/where to go get it. null for items where "I don't have
 *               this" isn't a meaningful state (e.g. skip is the only option,
 *               or the item is a "gate" kind)
 *
 * NOT included yet: time-saved copy per item. Don't fabricate numbers —
 * source real figures (e.g. published research on executor time burden)
 * before writing that copy in. Placeholder field `timeSavedCopy: null`
 * left in place so it's a one-line fill-in later without touching the
 * shape of this file.
 *
 * NOTE on crypto: VAULT_SUBCATEGORIES already has both
 * financial > "Cryptocurrency" and digital > "Crypto Wallets". Rather than
 * merge them (which would touch existing category data), this file treats
 * them as two genuinely different asks:
 *   - financial_crypto_holdings = WHAT you own (coins, amount, exchange)
 *   - digital_crypto_access     = HOW to access it (seed phrase, private
 *     key, hardware wallet location)
 * Recommend renaming the VAULT_SUBCATEGORIES digital label from
 * "Crypto Wallets" to "Crypto Wallet Access / Seed Phrases" so the
 * distinction is visible in the UI too, not just in this file.
 */

const VAULT_GUIDED_ITEMS = [

  // ---------- TIER 1 — Immediate access (first 72 hours) ----------
  {
    id: "will_last_will",
    category: "will",
    subcategory: "Last Will & Testament",
    tier: 1,
    conditional: null,
    prompt: "Do you have a signed will?",
    noDocGuidance: "Contact an estate planning attorney to draft one, or use HeirOS's AI Will Builder to get started.",
    timeSavedCopy: null,
  },
  {
    id: "will_poa_financial",
    category: "will",
    subcategory: "Power of Attorney",
    tier: 1,
    conditional: null,
    prompt: "Do you have a Financial or General Power of Attorney document?",
    noDocGuidance: "Contact an estate planning attorney to have one drafted.",
    timeSavedCopy: null,
  },
  {
    id: "will_healthcare_directive",
    category: "will",
    subcategory: "Healthcare Directive / Living Will",
    tier: 1,
    conditional: null,
    prompt: "Do you have an Advance Directive or Living Will on file?",
    noDocGuidance: "Ask your primary care doctor's office (many provide a free state-specific form) or an estate attorney.",
    timeSavedCopy: null,
  },
  {
    id: "medical_poa_doc",
    category: "medical",
    subcategory: "Medical Power of Attorney Document",
    tier: 1,
    conditional: null,
    prompt: "Do you have a Medical/Healthcare Power of Attorney naming who can make medical decisions for you?",
    noDocGuidance: "Contact an estate attorney, or ask your hospital/doctor's office for a state-specific healthcare POA form.",
    timeSavedCopy: null,
  },
  // INTENTIONALLY OMITTED FROM THE GUIDED FLOW: medical > "Advance Directive /
  // Living Will" is a real VAULT_SUBCATEGORIES entry (confirmed still present
  // and untouched, no data change) but is the same real-world document as
  // will_healthcare_directive above ("Healthcare Directive / Living Will").
  // Live-tested this session and confirmed it asked the user the identical
  // question twice back-to-back — removed from this array rather than shipping
  // a double-ask. The subcategory itself is still fully usable via the normal
  // "+ Add to Vault" flow; it's only excluded from the *guided* prompt queue.
  // If you'd rather merge the two VAULT_SUBCATEGORIES entries into one at some
  // point, that's a separate, deliberate data change — not done here.
  {
    id: "financial_checking_savings",
    category: "financial",
    subcategory: "Checking & Savings",
    tier: 1,
    conditional: null,
    prompt: "Do you have your primary checking/savings account info recorded?",
    noDocGuidance: "Check a recent bank statement, or log into your bank's app or website.",
    timeSavedCopy: null,
  },
  {
    id: "contacts_attorney",
    category: "contacts",
    subcategory: "Attorneys",
    tier: 1,
    conditional: null,
    prompt: "Do you have contact info for an attorney (estate, business, or otherwise)?",
    noDocGuidance: "Ask friends or family for a referral, or use your state bar association's attorney referral service.",
    timeSavedCopy: null,
  },
  {
    id: "other_safe_combo",
    category: "other",
    subcategory: "Safe Combinations / Lock Codes",
    tier: 1,
    conditional: null,
    prompt: "Do you have a safe, lockbox, or storage unit your executor would need to access?",
    noDocGuidance: null, // not applicable if the user has none — skip only
    timeSavedCopy: null,
  },

  // ---------- TIER 2 — Financial & legal backbone ----------
  {
    id: "will_trust",
    category: "will",
    subcategory: "Trust Documents",
    tier: 2,
    conditional: null,
    prompt: "Do you have a living trust or other trust documents?",
    noDocGuidance: "Contact the estate attorney who set it up, or an estate planning attorney if you're unsure whether you have one.",
    timeSavedCopy: null,
  },
  {
    id: "will_beneficiary",
    category: "will",
    subcategory: "Beneficiary Designations",
    tier: 2,
    conditional: null,
    prompt: "Do you have a record of your beneficiary designations (401k, IRA, life insurance)?",
    noDocGuidance: "Log into each account provider's site — beneficiary info is usually under account settings.",
    timeSavedCopy: null,
  },
  {
    id: "financial_investment",
    category: "financial",
    subcategory: "Investment & Brokerage",
    tier: 2,
    conditional: null,
    prompt: "Do you have investment or brokerage accounts?",
    noDocGuidance: "Check a recent statement or your brokerage's app or website.",
    timeSavedCopy: null,
  },
  {
    id: "financial_retirement",
    category: "financial",
    subcategory: "Retirement (401k, IRA)",
    tier: 2,
    conditional: null,
    prompt: "Do you have retirement accounts (401k, IRA, pension)?",
    noDocGuidance: "Contact your current or former employer's HR/benefits department, or search the National Registry of Unclaimed Retirement Benefits for old accounts.",
    timeSavedCopy: null,
  },
  {
    id: "financial_crypto_holdings",
    category: "financial",
    subcategory: "Cryptocurrency",
    tier: 2,
    conditional: null,
    prompt: "Do you own any cryptocurrency? Record what you hold and where (exchange or wallet).",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "financial_loans_debts",
    category: "financial",
    subcategory: "Loans & Debts",
    tier: 2,
    conditional: null,
    prompt: "Do you have outstanding loans or debts your executor should know about (mortgage, personal loans)?",
    noDocGuidance: "Check a recent loan statement or your lender's website.",
    timeSavedCopy: null,
  },
  {
    id: "financial_credit_cards",
    category: "financial",
    subcategory: "Credit Cards",
    tier: 2,
    conditional: null,
    prompt: "Do you have credit card accounts to record?",
    noDocGuidance: "Check a recent statement.",
    timeSavedCopy: null,
  },
  {
    id: "insurance_life",
    category: "insurance",
    subcategory: "Life Insurance",
    tier: 2,
    conditional: null,
    prompt: "Do you have a life insurance policy?",
    noDocGuidance: "Contact your insurance agent or the carrier directly. If you're trying to find a deceased relative's policy, MIB Group's Policy Locator Service can help.",
    timeSavedCopy: null,
  },
  {
    id: "property_real_estate",
    category: "property",
    subcategory: "Real Estate",
    tier: 2,
    conditional: null,
    prompt: "Do you own real estate?",
    noDocGuidance: "Contact your county recorder's or assessor's office for a copy of your deed.",
    timeSavedCopy: null,
  },
  {
    id: "property_vehicles",
    category: "property",
    subcategory: "Vehicles",
    tier: 2,
    conditional: null,
    prompt: "Do you have vehicle titles to record?",
    noDocGuidance: "Contact your state DMV for a duplicate title.",
    timeSavedCopy: null,
  },
  {
    // Gate item — no upload, no vault_items row, no real subcategory.
    // Answer is held client-side in the guided-flow component's own state
    // (or localStorage), not persisted to estates/profiles. Answering "No"
    // hides the four conditional:"has_business" items below for this
    // session/device; answering "Yes" reveals them. Re-asked on each device/
    // browser until a persisted answer exists, which is an acceptable
    // tradeoff to avoid a schema change for this v1.
    id: "business_owner_gate",
    category: "business",
    subcategory: null,
    kind: "gate",
    tier: 2,
    conditional: null,
    prompt: "Do you own a business or have an ownership stake in one?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "business_llc",
    category: "business",
    subcategory: "LLCs & Corporations",
    kind: "document",
    tier: 2,
    conditional: "has_business",
    prompt: "Do you have LLC or corporation formation documents?",
    noDocGuidance: "Contact the attorney or accountant who formed the entity, or your Secretary of State's business filings office.",
    timeSavedCopy: null,
  },
  {
    id: "business_partnership",
    category: "business",
    subcategory: "Partnerships",
    kind: "document",
    tier: 2,
    conditional: "has_business",
    prompt: "Do you have partnership agreements?",
    noDocGuidance: "Contact your business attorney or co-owners.",
    timeSavedCopy: null,
  },
  {
    id: "business_ip",
    category: "business",
    subcategory: "Intellectual Property",
    kind: "document",
    tier: 2,
    conditional: "has_business",
    prompt: "Do you hold any patents, trademarks, or copyrights tied to your business?",
    noDocGuidance: "Contact the attorney who filed it, or search the USPTO database.",
    timeSavedCopy: null,
  },
  {
    id: "business_agreements",
    category: "business",
    subcategory: "Business Agreements",
    kind: "document",
    tier: 2,
    conditional: "has_business",
    prompt: "Do you have buy-sell agreements or other key business agreements?",
    noDocGuidance: "Contact your business attorney.",
    timeSavedCopy: null,
  },

  // ---------- TIER 3 — Completeness ----------
  {
    id: "insurance_health",
    category: "insurance",
    subcategory: "Health Insurance",
    tier: 3,
    conditional: null,
    prompt: "Do you have your health insurance policy info recorded?",
    noDocGuidance: "Contact your insurance provider or HR/benefits department.",
    timeSavedCopy: null,
  },
  {
    id: "insurance_home_auto",
    category: "insurance",
    subcategory: "Home & Auto",
    tier: 3,
    conditional: null,
    prompt: "Do you have homeowners/renters and auto insurance policies recorded?",
    noDocGuidance: "Contact your insurance agent or carrier.",
    timeSavedCopy: null,
  },
  {
    id: "insurance_ltc",
    category: "insurance",
    subcategory: "Long-term Care",
    tier: 3,
    conditional: null,
    prompt: "Do you have a long-term care insurance policy?",
    noDocGuidance: "Contact your insurance agent or carrier.",
    timeSavedCopy: null,
  },
  {
    id: "insurance_other",
    category: "insurance",
    subcategory: "Other Policies",
    tier: 3,
    conditional: null,
    prompt: "Do you have any other insurance policies (umbrella, disability, pet, etc.)?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "property_personal_property",
    category: "property",
    subcategory: "Valuable Personal Property",
    tier: 3,
    conditional: null,
    prompt: "Do you have valuable personal property (jewelry, art, collectibles) worth documenting or appraising?",
    noDocGuidance: "A certified appraiser can help establish documented value.",
    timeSavedCopy: null,
  },
  {
    id: "property_storage",
    category: "property",
    subcategory: "Storage Units",
    tier: 3,
    conditional: null,
    prompt: "Do you rent any storage units?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "digital_email",
    category: "digital",
    subcategory: "Email Accounts",
    tier: 3,
    conditional: null,
    prompt: "Do you want to record your primary email account access?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "digital_social",
    category: "digital",
    subcategory: "Social Media",
    tier: 3,
    conditional: null,
    prompt: "Do you want to record social media account access or memorialization preferences?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "digital_subscriptions",
    category: "digital",
    subcategory: "Subscriptions",
    tier: 3,
    conditional: null,
    prompt: "Do you have recurring subscriptions your executor should know to cancel?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "digital_domains",
    category: "digital",
    subcategory: "Domain Names & Websites",
    tier: 3,
    conditional: null,
    prompt: "Do you own any domain names or websites?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "digital_crypto_access",
    // See NOTE at top of file re: recommended subcategory label rename
    // from "Crypto Wallets" to "Crypto Wallet Access / Seed Phrases".
    category: "digital",
    subcategory: "Crypto Wallets",
    tier: 3,
    conditional: null,
    prompt: "Do you have crypto wallet access info (seed phrase, private key, hardware wallet location) to record?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "digital_storage",
    category: "digital",
    subcategory: "Online Storage",
    tier: 3,
    conditional: null,
    prompt: "Do you use online/cloud storage your executor should know about?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "medical_conditions",
    category: "medical",
    subcategory: "Diagnoses & Conditions",
    tier: 3,
    conditional: null,
    prompt: "Do you want to record current diagnoses or medical conditions?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "medical_medications",
    category: "medical",
    subcategory: "Medications",
    tier: 3,
    conditional: null,
    prompt: "Do you want to record current medications?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "medical_doctors",
    category: "medical",
    subcategory: "Doctors & Providers",
    tier: 3,
    conditional: null,
    prompt: "Do you want to record your doctors and providers?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "medical_insurance_cards",
    category: "medical",
    subcategory: "Insurance Cards",
    tier: 3,
    conditional: null,
    prompt: "Do you want to upload photos of your insurance cards?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "medical_organ_donation",
    category: "medical",
    subcategory: "Organ Donation Wishes",
    tier: 3,
    conditional: null,
    prompt: "Do you want to record your organ donation wishes?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "contacts_financial_advisor",
    category: "contacts",
    subcategory: "Financial Advisors",
    tier: 3,
    conditional: null,
    prompt: "Do you have a financial advisor's contact info to add?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "contacts_accountant",
    category: "contacts",
    subcategory: "Accountants",
    tier: 3,
    conditional: null,
    prompt: "Do you have an accountant or CPA's contact info to add?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "contacts_doctor",
    category: "contacts",
    subcategory: "Doctors",
    tier: 3,
    conditional: null,
    prompt: "Do you have your doctor(s)' contact info to add?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "contacts_insurance_agent",
    category: "contacts",
    subcategory: "Insurance Agents",
    tier: 3,
    conditional: null,
    prompt: "Do you have an insurance agent's contact info to add?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "contacts_clergy",
    category: "contacts",
    subcategory: "Clergy / Spiritual Advisors",
    tier: 3,
    conditional: null,
    prompt: "Do you have a clergy member or spiritual advisor's contact info to add?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "other_memberships",
    category: "other",
    subcategory: "Memberships",
    tier: 3,
    conditional: null,
    prompt: "Do you have club, association, or other memberships to record?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "other_warranties",
    category: "other",
    subcategory: "Warranties & Receipts",
    tier: 3,
    conditional: null,
    prompt: "Do you have important warranties or receipts to record?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "other_misc",
    category: "other",
    subcategory: "Miscellaneous",
    tier: 3,
    conditional: null,
    prompt: "Anything else important your executor should know about?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },

  // ---------- LEGACY — excluded from tiered progress/time-saved model ----------
  // Rendered as a separate "for your family" section, not part of the
  // priority-ordered guided flow or the time-saved messaging.
  {
    id: "personal_letters_family",
    category: "personal",
    subcategory: "Letters to Family",
    tier: "legacy",
    conditional: null,
    prompt: "Would you like to leave a letter for your family?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "personal_letters_friends",
    category: "personal",
    subcategory: "Letters to Friends",
    tier: "legacy",
    conditional: null,
    prompt: "Would you like to leave a letter for friends?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "personal_final_wishes",
    category: "personal",
    subcategory: "Final Wishes / Funeral Preferences",
    tier: "legacy",
    conditional: null,
    prompt: "Would you like to record your final wishes or funeral preferences?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
  {
    id: "personal_video_messages",
    category: "personal",
    subcategory: "Video Messages",
    tier: "legacy",
    conditional: null,
    prompt: "Would you like to leave a video message?",
    noDocGuidance: null,
    timeSavedCopy: null,
  },
];

export default VAULT_GUIDED_ITEMS;
